import BlacklistApiRepository from '@/services/skills/BlacklistApiRepository';
import { findBlacklistMatch } from '@/utils/blacklistMatcher';

const state = () => ({
    blacklist: JSON.parse(localStorage.getItem('blacklist')) || [],
    isLoading: false,
    error: null,
    searchQuery: '',
});

const getters = {
    getBlacklist: (state) => state.blacklist,
    getIsLoading: (state) => state.isLoading,
    getError: (state) => state.error,
    getSearchQuery: (state) => state.searchQuery,

    // Adapter to transform API data to table format
    getAdaptedBlacklist: (state) => {
        if (!state.blacklist || !state.blacklist.items) {
            return [];
        }

        return state.blacklist.items.map((item, index) => {
            if (!item.value) {
                return {
                    id: `item-${index}`,
                    type: 'Unknown',
                    value: 'Unknown',
                };
            }
            // Infer type from value structure
            const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.value);
            const isPhone = /^[+]?[1-9][\d]{0,15}$/.test(item.value.replace(/[\s\-()]/g, ''));

            const type = isEmail ? 'email' : isPhone ? 'phone' : 'Other';

            return {
                id: `item-${index}`, // Generate temporary ID since API doesn't provide one
                type: type,
                value: item.value,
                dateAdded: new Date(item.date_created * 1000).toISOString(), // Convert timestamp to ISO string
            };
        });
    },

    // Filtered blacklist based on search query
    getFilteredBlacklist: (state, getters) => {
        if (!state.searchQuery) return getters.getAdaptedBlacklist;

        const query = state.searchQuery.toLowerCase();
        return getters.getAdaptedBlacklist.filter(
            (item) => item.value.toLowerCase().includes(query) || item.type.toLowerCase().includes(query),
        );
    },
    // Matching is format-agnostic (see blacklistMatcher): an entry saved as
    // "+31 6 57177135" blocks the room whose channelId is "+31657177135", and the
    // badge / remove action must agree with what the backend actually blocks.
    findBlacklistItem: (state) => (value) => {
        return findBlacklistMatch(state.blacklist?.items, value);
    },
    findFilteredBlacklistItem: (state, getters) => (value) => {
        return findBlacklistMatch(getters.getFilteredBlacklist, value);
    },
};

const actions = {
    async fetchBlacklist({ commit }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const response = await BlacklistApiRepository.getBlacklist();
            commit('SET_BLACKLIST', response || []);
        } catch (err) {
            console.error('Error fetching blacklist:', err);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async addToBlacklist({ dispatch }, newItem) {
        try {
            const request = {
                item: newItem.value,
            };

            await BlacklistApiRepository.addToBlacklist(request);

            // Refresh data to get the updated list from server
            await dispatch('fetchBlacklist');

            return { success: true };
        } catch (err) {
            console.error('Error adding item to blacklist:', err);
            return { success: false, error: err };
        }
    },

    async removeFromBlacklist({ commit, getters, state }, id) {
        // Find the item by the generated ID to get its value
        const adaptedBlacklist = getters.getAdaptedBlacklist;
        const itemToRemove = adaptedBlacklist.find((item) => item.id === id);

        if (!itemToRemove) {
            return { success: false, error: 'Item not found' };
        }

        const previousState = { ...state.blacklist };

        // Remove from local state immediately for optimistic update
        commit('SET_BLACKLIST', {
            ...state.blacklist,
            items: state.blacklist.items.filter((item) => item.value !== itemToRemove.value),
        });

        try {
            const request = {
                item: itemToRemove.value,
                toJSON() {
                    return { item: this.item };
                },
            };

            await BlacklistApiRepository.removeFromBlacklist(request);
            return { success: true };
        } catch (err) {
            console.error('Error removing item from blacklist:', err);
            // Revert optimistic update
            commit('SET_BLACKLIST', previousState);
            return { success: false, error: err };
        }
    },

    setSearchQuery({ commit }, query) {
        commit('SET_SEARCH_QUERY', query);
    },

    clearSearchQuery({ commit }) {
        commit('SET_SEARCH_QUERY', '');
    },

    clearState({ commit }) {
        commit('SET_BLACKLIST', []);
        commit('SET_LOADING', false);
        commit('SET_ERROR', null);
        commit('SET_SEARCH_QUERY', '');
        localStorage.removeItem('blacklist');
        localStorage.removeItem('blacklistIsLoading');
        localStorage.removeItem('blacklistError');
        localStorage.removeItem('blacklistSearchQuery');
    },
};

const mutations = {
    SET_BLACKLIST(state, blacklist) {
        state.blacklist = blacklist;
        localStorage.setItem('blacklist', JSON.stringify(blacklist));
    },

    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },

    SET_ERROR(state, error) {
        state.error = error;
    },

    SET_SEARCH_QUERY(state, searchQuery) {
        state.searchQuery = searchQuery;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
